import { FC, MutableRefObject, useEffect, useRef, useState } from 'react';
import {
  SpheraConfirmation,
  SpheraHeader,
} from '@sphera-cloud/component-ui-reactjs';
import { SubTab, Tab } from 'lca-common-ui-components';
import moment from 'moment';
import { TasksGrid } from 'src/components/TasksGrid';
import { FilesGrid } from 'src/components/FilesGrid';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { clearJob, deleteJob, fetchJob, isLoadingSelector, jobSelector, updateJob } from 'src/store/slices/jobs';
import { useNavigate, useParams } from 'react-router-dom';
import { useSpinnerContext } from 'src/context/SpinnerContext';
import { menuSelector, setMenu } from 'src/store/slices/menus';
import { EditJobModal } from 'src/pages/JobDetails/components/EditJobModal';
import { Job } from 'src/store/slices/jobs/types';
import { ObjectIdentifier } from 'src/components/ObjectIdentifier/ObjectIdentifier';
import { TruncateTextSimple } from 'src/components/TruncateTextSimple';
import { useUserSettings } from 'src/hooks/useUserSettings';

export const JobDetails: FC = () => {
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [editModal, setEditModal] = useState<boolean>(false);
  const [valueContainerStyle, setValueContainerStyle] = useState({});
  const { getFormattedDateTime, getFormattedExpirationDateTime } = useUserSettings();

  const { jobId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setShowSpinner } = useSpinnerContext();

  const job = useAppSelector(jobSelector);
  const isLoading = useAppSelector(isLoadingSelector);
  const { menu } = useAppSelector(menuSelector);

  const timeoutRef: MutableRefObject<number | null> = useRef(null);

  const headerData = [
    {
      title: 'Name',
      value: job.name,
    },
    {
      title: 'Id',
      value: job.jobId,
    },
    {
      title: 'Type',
      value: job.type,
    },
    {
      title: 'Status',
      value: job.status,
    },
    {
      title: 'Priority',
      value: job.priority,
    },
    {
      title: 'Creation Date',
      value: getFormattedDateTime(job.creationDateTime),
    },
    {
      title: 'Expires On',
      value: getFormattedExpirationDateTime(job.expirationDate),
    },
  ];

  useEffect(() => {
    dispatch(fetchJob({ id: jobId ?? '', timeoutRef }));
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = 0;
      dispatch(clearJob(undefined));
    };
  }, []);

  useEffect(() => {
    setShowSpinner(isLoading);
  }, [isLoading]);

  useEffect(() => {
    const metadataListWidth = document.querySelector('ul.metadata-list')?.clientWidth ?? 0;
    const itemWidth = metadataListWidth / headerData.length;
    const valueContainerStyle = { maxWidth: `${itemWidth}px` };
    setValueContainerStyle(valueContainerStyle);
  }, []);

  const navigateToPage = (link: string) => {
    navigate(link);
    if (!menu) return;
    const newMenu: any = [...menu];
    newMenu[3] = { ...menu[3], selected: true };
    dispatch(setMenu(newMenu));
  };

  const removeJob = async (id: string) => {
    await dispatch(deleteJob(id));
    setConfirmation(false);
    navigateToPage('/jobs');
  };

  const onSaveJob = async (data: Job) => {
    const date = data.expirationDate ? moment(data.expirationDate).format('YYYY-MM-DDTHH:mm:ssZ') : '';
    await dispatch(updateJob({
      id: data.jobId as string,
      date,
      propertyName: 'expirationdate',
    }));
    dispatch(fetchJob({ id: jobId ?? '' }));
    setEditModal(false);
  };

  const mappedHeaderData = headerData?.map(item => ({
    title: item.title,
    value: (
      <div className="value-container" style={valueContainerStyle}>
        {item.title === 'Id' ? (
          <ObjectIdentifier id={item.value as string} />
        ) : (
          <TruncateTextSimple
            text={item.value as string}
            tooltipText={item.value as string}
          />
        )}
      </div>
    ),
  }));

  return (
    <div className="inner-content">
      <SpheraHeader
        text="Job Details"
        navigate={navigate}
        breadcrumbData={[
          { id: 1, text: 'Sphera Cloud LCA', link: '/' },
          {
            id: 2,
            text: 'Jobs',
            link: '/jobs',
          },
          {
            id: 3,
            text: 'Job Details',
            link: `/jobs/${job.jobId}`,
          },
        ]}
        button={[
          {
            type: 'primary',
            icon: '',
            text: 'Set Expiration',
            onClick: () => {
              setEditModal(true);
            },
            tooltip: '',
            position: '',
          },
          {
            type: 'primary',
            icon: '',
            text: 'Delete',
            onClick: () => {
              setConfirmation(true);
            },
            tooltip: '',
            position: '',
          },
        ]}
        metadata={job ? mappedHeaderData : []}
      />
      <Tab className="job-details__tab-wrap">
        <SubTab title="Tasks">
          <div id={`tasks-${job.jobId}`} className="full-height">
            <TasksGrid tasks={job.tasks || []} />
          </div>
        </SubTab>
        <SubTab title="Files">
          <div id={`files-${job.jobId}`} className="full-height">
            <FilesGrid files={job.files || []} />
          </div>
        </SubTab>
      </Tab>
      {confirmation && (
        <SpheraConfirmation
          hasAction
          isDangerConfirm
          actionText="Delete"
          onCancel={() => setConfirmation(false)}
          onAction={() => removeJob(job.jobId ?? '')}
          title="Delete this job"
        >
          Once you delete <b>{job.name}</b> job, it will be removed forever. Are you sure?
        </SpheraConfirmation>
      )}

      {editModal && (
        <EditJobModal
          closeModal={() => setEditModal(false)}
          details={job}
          onAction={onSaveJob}
        />
      )}
    </div>
  );
};
